import { ReadMoreLink } from '@/components/common/ReadMoreLink'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { usePage } from '@/components/providers/PageProvider'
import { type RecType } from '@/defs/rec'
import { useCardsRec } from '@/hooks/useCardsRec'
import { formatSalt } from '@/utils/formatUtil'
import { getRecName } from '@/utils/recUtil'

import { StatCommonBox } from '../../StatCommon/StatCommonBox/StatCommonBox'
import { StatCommonFigure } from '../../StatCommon/StatCommonFigure/StatCommonFigure'

type Props = {
  rec: RecType
}

export const StatRecSalt = ({ rec }: Props) =>
  rec.saltRec !== undefined ? <StatRecSaltRec rec={rec} /> : <StatRecSaltGlobal rec={rec} />

const StatRecSaltRec = ({ rec }: Props) => {
  const { page } = usePage()
  const subject = page?.metadata?.subject
  const label = subject ? `Salt (${subject})` : 'Yearly Salt'

  return <StatRecSaltInner label={label} rec={rec} salt={rec.saltRec} subject={subject} />
}

const StatRecSaltGlobal = ({ rec }: Props) => {
  const { card, loading } = useCardsRec({ rec })

  if (loading) return <StatCommonBox label='Salt' loading />

  return <StatRecSaltInner label='Salt' rec={rec} salt={card?.salt} />
}

type StatRecSaltInnerProps = {
  label: string
  rec: RecType
  salt?: number
  subject?: string
}

const StatRecSaltInner = ({ label, rec, salt, subject }: StatRecSaltInnerProps) => {
  const fullName = getRecName(rec)

  const tooltip = (
    <>
      <h2>
        <IconWithLabel iconBefore='salt'>Salt Score</IconWithLabel>
      </h2>
      <p>
        <strong>Salt scores</strong> are voted on annually by the community, on a scale of 0 (least salty) to 4 (most
        salty).{' '}
        {salt !== undefined ? (
          <>
            <strong>{fullName}</strong> has a salt score of <strong>{formatSalt(salt)}</strong>
            {subject ? <span>{` in ${subject}`}</span> : <></>}
            <span>.</span>
          </>
        ) : (
          <>
            <strong>{fullName}</strong>
            <span> has not been voted on.</span>
          </>
        )}
      </p>
      <p>
        <ReadMoreLink as='LinkOutbound' href='/top/salt' />
      </p>
    </>
  )

  return (
    <StatCommonBox label={label} popover={tooltip}>
      <StatCommonFigure>
        <IconWithLabel iconBefore='salt' iconBeforeMargin='xs' iconBeforeSize='xs'>
          {formatSalt(salt)}
        </IconWithLabel>
      </StatCommonFigure>
    </StatCommonBox>
  )
}
